import { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import { Col, Table, Badge } from 'react-bootstrap';
import { getPayMethods } from '../../../../usecases/payments-usecase';
import { lang } from '../../../../utils/langs';
import { formatMoney } from '../../../../utils/formating';
import ReceivableAccountSelector from './ReceivableAccountSelector';
import { dispatchCustomEvent } from '../../../../utils/dispatch';
import { User, FileText, Calendar, DollarSign, History, AlertCircle, CheckCircle2, CreditCard, FileSignature } from 'lucide-react';
import '../style.css';

/**
 * Formulario mejorado para registrar abonos a cuentas por cobrar.
 * Muestra información detallada de la cuenta, historial de transacciones y cálculo de saldos.
 * 
 * @param {Object} props
 * @param {Object} props.data - Datos del formulario
 * @param {Object} props.errors - Errores de validación
 * @param {Function} props.onChange - Callback para cambios en el formulario
 */
function ReceivablePaymentForm({ data, errors, onChange }) {
    const [paymethods, setPayMethods] = useState([]);

    useEffect(() => {
        fetchPayMethods();
    }, []);

    async function fetchPayMethods() {
        try {
            const result = await getPayMethods();
            setPayMethods(result);
        } catch (err) {
            console.error("Error fetching payment methods:", err);
            dispatchCustomEvent("popup", {
                message: "Error al cargar métodos de pago",
                type: "error"
            });
        }
    }

    function handleChange(e) {
        const { id, value } = e.target;
        const key = id.replace("receivable-", "");

        // Si cambia el método de pago, guardar también el nombre
        if (key === 'paymethod_id') {
            const selectedMethod = paymethods.find(m => m.id === parseInt(value));
            onChange({
                ...data,
                [key]: value,
                payment_method_name: selectedMethod ? lang('es', selectedMethod.method) : ''
            });
        } else {
            onChange({ ...data, [key]: value });
        }
    }

    function handleAccountSelect(account) {
        if (!account) {
            // Limpiar selección
            onChange({
                account_id: null,
                amount: '',
                paymethod_id: '',
                reference: '',
                current_balance: 0,
                third_name: '',
                third_id: null,
                third_identification: '',
                initial_amount: 0,
                bill_number: '',
                due_date: null,
                transactions: []
            });
            return;
        }

        onChange({
            ...data,
            account_id: account.id,
            current_balance: account.current_balance,
            third_name: account.third_name,
            third_id: account.third_id,
            third_identification: account.third_identification,
            initial_amount: account.initial_amount,
            bill_number: account.bill_number,
            due_date: account.due_date,
            transactions: account.transactions || []
        });
    }

    const newBalance = (data.current_balance || 0) - (parseFloat(data.amount) || 0);
    const totalPaid = (data.initial_amount || 0) - (data.current_balance || 0);
    const percentPaid = data.initial_amount > 0 ? ((totalPaid / data.initial_amount) * 100).toFixed(1) : 0;

    return (
        <>
            <ReceivableAccountSelector
                selectedAccountId={data.account_id}
                onSelect={handleAccountSelect}
                error={errors.account_id}
            />

            {data.account_id && (
                <div className="receivable-layout">
                    {/* Sección Izquierda: Info e Historial */}
                    <div className="receivable-left-col">

                        {/* Información de la Cuenta */}
                        <div className="receivable-premium-card">
                            <h6 className="receivable-card-title">
                                <FileText size={18} className="me-2" />
                                Información de la Cuenta
                            </h6>

                            <div className="receivable-grid-info">
                                <div className="info-block">
                                    <small>Cliente</small>
                                    <div className="info-value">
                                        <User size={14} className="me-1 text-muted" />
                                        {data.third_name}
                                    </div>
                                    <div className="info-sub">ID: {data.third_identification}</div>
                                </div>
                                <div className="info-block">
                                    <small>Factura Asociada</small>
                                    <div className="info-value text-primary">#{data.bill_number}</div>
                                    {data.due_date && (
                                        <div className="info-sub">
                                            <Calendar size={12} className="me-1" />
                                            Vence: {new Date(data.due_date).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <hr className="receivable-divider" />

                            <div className="receivable-financial-summary">
                                <div className="fin-box">
                                    <span className="fin-label">Monto Inicial</span>
                                    <span className="fin-amount dark">{formatMoney(data.initial_amount)}</span>
                                </div>
                                <div className="fin-box success">
                                    <span className="fin-label">Total Abonado</span>
                                    <span className="fin-amount">{formatMoney(totalPaid)}</span>
                                </div>
                                <div className="fin-box danger">
                                    <span className="fin-label">Saldo Actual</span>
                                    <span className="fin-amount">{formatMoney(data.current_balance)}</span>
                                </div>
                            </div>


                        </div>
                        <br></br>
                        {/* Historial de Transacciones */}
                        {data.transactions && data.transactions.length > 0 && (
                            <div className="receivable-premium-card">
                                <h6 className="receivable-card-title">
                                    <History size={18} className="me-2" />
                                    Historial de Abonos
                                </h6>
                                <div className="receivable-table-container">
                                    <Table borderless size="sm" className="mb-0 table-receivable-history">
                                        <thead>
                                            <tr>
                                                <th className="ps-2">FECHA</th>
                                                <th className="text-end">MONTO</th>
                                                <th className="text-center">MÉTODO</th>
                                                <th>REFERENCIA</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.transactions.map((tx) => (
                                                <tr key={tx.id}>
                                                    <td className="ps-2 text-muted small py-2">
                                                        {tx.date ? new Date(tx.date).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td className="text-end fw-bold text-success text-nowrap py-2">
                                                        + {formatMoney(tx.amount)}
                                                    </td>
                                                    <td className="text-center py-2">
                                                        <Badge className="receivable-badge">
                                                            {lang('es', tx.paymethod_name || tx.method)}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-muted small py-2">
                                                        {tx.reference || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sección Derecha: Formulario y Resumen */}
                    <div className="receivable-right-col">
                        <div className="receivable-premium-card highlight-border h-100 d-flex flex-column">
                            <h6 className="receivable-card-title text-primary">
                                <DollarSign size={18} className="me-2" />
                                Registrar Nuevo Abono
                            </h6>

                            <Form.Group controlId="receivable-amount" className="mb-4">
                                <Form.Label className="receivable-label">Monto a Abonar</Form.Label>
                                <div className="receivable-input-wrapper">
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        onChange={handleChange}
                                        value={data.amount || ''}
                                        isInvalid={!!errors.amount}
                                        className="receivable-input-amount premium-input"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.amount}
                                    </Form.Control.Feedback>
                                </div>
                                <Form.Text className="text-muted ms-1">
                                    Máx. permitido: {formatMoney(data.current_balance)}
                                </Form.Text>
                            </Form.Group>

                            <Form.Group controlId="receivable-paymethod_id" className="mb-4">
                                <Form.Label className="receivable-label">Método de Pago</Form.Label>
                                <div className="receivable-input-wrapper">
                                    <CreditCard className="receivable-input-icon text-muted" size={20} />
                                    <Form.Select
                                        onChange={handleChange}
                                        value={data.paymethod_id || ''}
                                        isInvalid={!!errors.paymethod_id}
                                        className="premium-input ps-5"
                                    >
                                        <option value="">Selecciona un método</option>
                                        {paymethods?.map(method => {
                                            if (method.id === 4) return null;
                                            return (
                                                <option key={method.id} value={method.id}>
                                                    {lang('es', method.method)}
                                                </option>
                                            )
                                        })}
                                    </Form.Select>
                                </div>
                                <Form.Control.Feedback type="invalid">
                                    {errors.paymethod_id}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group controlId="receivable-reference" className="mb-4">
                                <Form.Label className="receivable-label">Referencia <span className="text-muted fw-normal">(Opcional)</span></Form.Label>
                                <div className="receivable-input-wrapper">
                                    <FileSignature className="receivable-input-icon text-muted" size={20} />
                                    <Form.Control
                                        type="text"
                                        placeholder="No. Transacción / Cheque"
                                        onChange={handleChange}
                                        value={data.reference || ''}
                                        className="premium-input ps-5"
                                    />
                                </div>
                            </Form.Group>

                            {/* Resumen Dinámico que se pega al fondo */}
                            <div className="mt-auto">
                                {data.amount && parseFloat(data.amount) > 0 && (
                                    <div className={`receivable-summary-panel ${newBalance === 0 ? 'success-mode' : ''}`}>
                                        <h6 className="summary-title mb-2">
                                            Proyección Final
                                        </h6>

                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="receivable-text-sub">Saldo Anterior:</span>
                                            <span className="fw-medium receivable-text-main">{formatMoney(data.current_balance)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="receivable-text-sub">Abono:</span>
                                            <span className="fw-bold text-success">-{formatMoney(parseFloat(data.amount))}</span>
                                        </div>

                                        <hr className="my-2 border-secondary opacity-25" />

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-bold receivable-text-main">Nuevo Saldo:</span>
                                            <span className={`fs-5 fw-bold ${newBalance === 0 ? 'text-success' : 'text-danger'}`}>
                                                {formatMoney(newBalance)}
                                            </span>
                                        </div>

                                        {newBalance === 0 && (
                                            <div className="summary-alert success mt-2">
                                                <CheckCircle2 size={16} className="me-2" />
                                                <span>La cuenta quedará saldada</span>
                                            </div>
                                        )}

                                        {newBalance < 0 && (
                                            <div className="summary-alert error mt-2">
                                                <AlertCircle size={16} className="me-2" />
                                                <span>El monto excede el límite</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default ReceivablePaymentForm;
